import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tags } from "lucide-react";
import { toast } from "sonner";

const sel = "h-8 rounded-md border border-slate-200 bg-white px-2 text-sm";

// Gives "Candidate (stage not set)" people a real recruiting stage via the normal opportunity endpoints.
export function AssignStagesPanel({ rows, onDone }) {
  const [pipes, setPipes] = useState([]);
  const [pipeId, setPipeId] = useState("");
  const [pick, setPick] = useState({});
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/pipelines").then((r) => {
      const items = (r.data.items || []).filter((p) => p.stages?.length);
      setPipes(items);
      setPipeId((items.find((p) => p.is_default) || items[0] || {}).id || "");
    }).catch(() => {});
  }, []);
  const stages = [...((pipes.find((p) => p.id === pipeId) || {}).stages || [])].sort((a, b) => a.order - b.order);
  const chosen = Object.entries(pick).filter(([, s]) => s);
  const applyBulk = (sid) => {
    if (sid && rows.length > 10 && !window.confirm(`Set all ${rows.length} people to this stage? You can still change individual rows before saving.`)) return;
    setBulk(sid); setPick(Object.fromEntries(rows.map((r) => [r.pid, sid])));
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reports/recruiting/assign-stages", { pipeline_id: pipeId, items: chosen.map(([person_id, stage_id]) => ({ person_id, stage_id })) });
      toast.success(`${data.assigned} stage${data.assigned === 1 ? "" : "s"} assigned`);
      if (data.errors?.length) toast.error(`${data.errors.length} could not be assigned: ${data.errors[0].error}`);
      setPick({}); setBulk(""); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!rows.length) return <p className="text-sm text-emerald-700 mb-4" data-testid="assign-stages-none">Every active candidate has a recruiting stage.</p>;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 mb-5" data-testid="assign-stages-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Tags className="w-4 h-4 text-amber-700" />
        <h3 className="font-semibold text-slate-800">Needs a stage ({rows.length})</h3>
        <span className="text-xs text-slate-500">Pick a stage for each person — this creates or updates their recruiting opportunity.</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select className={sel} value={pipeId} onChange={(e) => { setPipeId(e.target.value); setPick({}); setBulk(""); }} data-testid="assign-pipeline">
            {pipes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className={sel} value={bulk} onChange={(e) => applyBulk(e.target.value)} data-testid="assign-bulk-stage">
            <option value="">Set all to…</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button size="sm" className="bg-brand hover:bg-brand-strong text-white" disabled={busy || !chosen.length} onClick={save} data-testid="assign-stages-save">
            {busy ? "Saving…" : `Assign ${chosen.length || ""} stage${chosen.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
      <div className="max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.pid} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`assign-row-${r.pid}`}>
            <span className="text-sm font-medium text-slate-800 min-w-[180px]">{r.name}</span>
            <span className="text-xs text-slate-500 flex-1">{r.program} · {r.recruiter} · last activity {r.last_touch ? r.last_touch.slice(0, 10) : "—"}</span>
            <select className={sel} value={pick[r.pid] || ""} onChange={(e) => setPick({ ...pick, [r.pid]: e.target.value })} data-testid={`assign-stage-${r.pid}`}>
              <option value="">Choose stage…</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
